/**
 * 애플리케이션 라우트 경로 상수
 */
export enum ROUTE_PATHS {
  LOGIN = '/',
  REGISTER = '/register',
  /**
   * 임시 비밀번호 로그인 후 강제 변경 (`mustChangePassword`).
   */
  FORCE_CHANGE_PASSWORD = '/force-change-password',
  ADMIN = '/admin',
  DASHBOARD = '/admin/dashboard',
  INSTITUTIONS = '/admin/institutions',
  /** TanStack Router 동적 세그먼트 — navigate 시 params.clinicId 사용 */
  INSTITUTION_DETAIL = '/admin/institutions/$clinicId',
  PHARMACIES = '/admin/pharmacies',
  PHARMACY_DETAIL = '/admin/pharmacies/$pharmacyId',
  PHARMA_COMPANIES = '/admin/pharma-companies',
  PHARMA_COMPANY_NEW = '/admin/pharma-companies/new',
  PHARMA_COMPANY_DETAIL = '/admin/pharma-companies/$pharmaCompanyId',
  DRUGS = '/admin/drugs',
  DRUG_DETAIL = '/admin/drugs/$drugCode',
  SETTLEMENT_FEES = '/admin/settlement-fees',
  SETTLEMENT_FEE_DETAIL = '/admin/settlement-fees/$drugCode',
  USERS = '/admin/users',
  USER_DETAIL = '/admin/users/$userId',
  /** 업체 관리 */
  PARTNER_COMPANY = '/admin/partner_company',
  PARTNER_COMPANY_NEW = '/admin/partner_company/new',
  PARTNER_COMPANY_DETAIL = '/admin/partner_company/$companyId',
  CLIENTS = '/admin/clients',
  CLIENT_DETAIL = '/admin/clients/$clientId',
  PRESCRIPTION_REVIEW = '/admin/prescriptions/review',
  PRESCRIPTION_REVIEW_DETAIL = '/admin/prescriptions/review/$prescriptionImageCode',
  /** 처방 검수 상세에서 새 창으로 여는 이전 처방 불러오기 */
  PRESCRIPTION_REVIEW_PREVIOUS_DRUGS = '/admin/prescriptions/review/$prescriptionImageCode/previous-drugs',
  PRESCRIPTION_HISTORY = '/admin/prescriptions/history',
  PRESCRIPTION_HISTORY_DETAIL = '/admin/prescriptions/history/$prescriptionId',
  /** 처방 마감일 설정 — 서버 admin_menu urlPath(V152)와 맞춘다 */
  PRESCRIPTION_REGISTRATION_DEADLINE = '/admin/prescriptionRegistrationDeadline',
  SETTLEMENT_UPLOAD = '/admin/settlements/upload',
  SETTLEMENT_UPLOAD_DETAIL = '/admin/settlements/upload/$pharmaCompanyId',
  SETTLEMENT_PRESCRIPTIONS = '/admin/settlements/prescriptions',
  SETTLEMENT_PRESCRIPTION_DETAIL = '/admin/settlements/prescriptions/$userEmail',
  BOARD_FAQ = '/admin/board/faq',
  BOARD_ANNOUNCEMENTS = '/admin/board/announcements',
  BOARD_ANNOUNCEMENT_NEW = '/admin/board/announcements/new',
  BOARD_ANNOUNCEMENT_EDIT = '/admin/board/announcements/$id',
  /** 약관 관리 — 서버 admin_menu의 TERMS urlPath(`/admin/terms`)와 맞춘다 */
  TERMS = '/admin/terms',
  TERMS_NEW = '/admin/terms/new',
  TERMS_DETAIL = '/admin/terms/$termsId',
  CONTRACT_APPENDIX = '/admin/contracts/appendix',
  BASE_FEE_RATES = '/admin/fee-rates/base',
  BASE_FEE_RATE_SETTING = '/admin/fee-rates/base/setting',
  DETAIL_FEE_RATES = '/admin/fee-rates/detail',
  DETAIL_FEE_RATE_SETTING = '/admin/fee-rates/detail/setting',
  REVERSE_TAX = '/admin/settlements/reverse-tax',
  DRUG_DATA_CREATE = '/admin/drugs/data-create',
  /** 맥스매치 Standard — lite/ops와 접두 충돌 방지를 위해 `/standard` 세그먼트 사용 */
  MAX_MATCH = '/admin/max-match/standard',
  MAX_MATCH_DETAIL = '/admin/max-match/standard/$userId',
  MAX_MATCH_FULL = '/admin/max-match/full',
  MAX_MATCH_FULL_DETAIL = '/admin/max-match/full/$maxMatchFullPrescriptionId',
  MAX_MATCH_MINI = '/admin/max-match/lite',
  MAX_MATCH_MINI_DETAIL = '/admin/max-match/lite/$summaryId',
  MAX_MATCH_OPS = '/admin/max-match/ops',
  MAX_MATCH_OPS_DETAIL = '/admin/max-match/ops/$summaryId',
  MAX_MATCH_OPERATING_HOURS = '/admin/max-match/operating-hours',
  SETTLEMENT_GENERATOR = '/admin/settlements/generator',
  COMMISSION_AGGREGATOR = '/admin/settlement-fees/aggregator',
  /**
   * 설정 모드 URL 베이스.
   * 이 prefix 아래에서는 일반 사업 사이드바 대신 설정 메뉴로 교체한다.
   */
  SETTINGS = '/admin/settings',
  /** 헤더 「내 프로필」 → 설정 모드 내 프로필 */
  MY_ACCOUNT = '/admin/settings/profile',
  /** 마스터·관리 전용 — 어드민 계정 관리 */
  SETTINGS_ACCOUNTS = '/admin/settings/accounts',
  /** 계정 상세 — `/admin/settings/accounts/$accountId` */
  SETTINGS_ACCOUNT_DETAIL = '/admin/settings/accounts/$accountId',
  /** 마스터·관리 전용 — 감사 로그 */
  SETTINGS_AUDIT_LOG = '/admin/settings/audit-log',
  /** ROLE_MASTER 전용 — 메뉴 설정 관리 */
  SETTINGS_MENUS = '/admin/settings/menus',
  /** 마스터·관리 전용 — 파트너스테이션 계정 관리 */
  SETTINGS_PARTNERS = '/admin/settings/partners',
  /** 시연 전용 URL 베이스 — 관리자 메뉴와 분리 */
  DEMO = '/demo',
  // 시연 홈 화면 경로. 베이스(DEMO)와 같은 값이지만 메뉴·라우트에서 의미가 달라 따로 둔다.
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  DEMO_HOME = '/demo',
  DEMO_POST_PROCESS = '/demo/post-process',
  DEMO_BATCH_JOBS = '/demo/batch-jobs',
  DEMO_BATCH_JOB_DETAIL = '/demo/batch-job-detail',
}

/** 설정 모드(사이드바 교체) URL 여부 */
export const isSettingsRoute = (pathname: string) =>
  pathname === ROUTE_PATHS.SETTINGS ||
  pathname.startsWith(`${ROUTE_PATHS.SETTINGS}/`);

/** 병의원 상세 경로 생성 */
export const institutionDetailPath = (clinicId: number) =>
  `/admin/institutions/${clinicId}`;

/** 문전약국 상세 경로 생성 */
export const pharmacyDetailPath = (pharmacyId: number) =>
  `/admin/pharmacies/${pharmacyId}`;

export const pharmaCompanyDetailPath = (pharmaCompanyId: number) =>
  `/admin/pharma-companies/${pharmaCompanyId}`;

/**
 * 경로 헬퍼는 path만 반환한다.
 * search(refYm 등)는 navigate({ search })로 따로 넘겨 목록 쿼리와 중복되지 않게 한다.
 */
export const drugDetailPath = (drugCode: string) =>
  `/admin/drugs/${encodeURIComponent(drugCode)}`;

export const settlementFeeDetailPath = (drugCode: string) =>
  `/admin/settlement-fees/${encodeURIComponent(drugCode)}`;

/**
 * 사용자 상세는 이메일이 아니라 사용자 ID 로 연다.
 * 탈퇴 후 같은 이메일로 재가입하면 계정이 여럿이라 이메일로는 최신 계정만 열린다.
 */
export const userDetailPath = (userId: number) => `/admin/users/${userId}`;

export const partnerCompanyDetailPath = (companyId: number | string) =>
  `/admin/partner_company/${companyId}`;

export const clientDetailPath = (clientId: number) =>
  `/admin/clients/${clientId}`;

export const prescriptionReviewDetailPath = (prescriptionImageCode: string) =>
  `/admin/prescriptions/review/${encodeURIComponent(prescriptionImageCode)}`;

export const prescriptionReviewPreviousDrugsPath = (
  prescriptionImageCode: string,
) => `${prescriptionReviewDetailPath(prescriptionImageCode)}/previous-drugs`;

/**
 * 새 창으로 여는 화면 — AdminLayout이 인증·권한만 확인하고 사이드바·헤더 없이 그린다.
 */
export const isPopupWindowRoute = (pathname: string) =>
  /^\/admin\/prescriptions\/review\/[^/]+\/previous-drugs\/?$/.test(pathname);

export const prescriptionHistoryDetailPath = (prescriptionId: number) =>
  `/admin/prescriptions/history/${prescriptionId}`;

export const settlementUploadDetailPath = (pharmaCompanyId: number) =>
  `/admin/settlements/upload/${pharmaCompanyId}`;

export const settlementPrescriptionDetailPath = (userEmail: string) =>
  `/admin/settlements/prescriptions/${encodeURIComponent(userEmail)}`;

export const baseFeeRateSettingPath = () => `/admin/fee-rates/base/setting`;

export const detailFeeRateSettingPath = () => `/admin/fee-rates/detail/setting`;

export const announcementEditPath = (id: number | string) =>
  `/admin/board/announcements/${id}`;

export const announcementViewPath = (id: number | string) =>
  `/admin/board/announcements/${id}`;

export const termsDetailPath = (termsId: number) => `/admin/terms/${termsId}`;

/** 약관 버전 등록 — 종류는 search(`?type=`)로 넘긴다 */
export const termsNewPath = () => `/admin/terms/new`;

export const maxMatchDetailPath = (userId: number) =>
  `/admin/max-match/standard/${userId}`;

export const maxMatchFullDetailPath = (maxMatchFullPrescriptionId: number) =>
  `/admin/max-match/full/${maxMatchFullPrescriptionId}`;

export const maxMatchMiniDetailPath = (summaryId: number) =>
  `/admin/max-match/lite/${summaryId}`;

export const maxMatchOpsDetailPath = (summaryId: number) =>
  `/admin/max-match/ops/${summaryId}`;

/** 설정 > 계정 상세 경로 */
export const settingsAccountDetailPath = (accountId: string) =>
  `/admin/settings/accounts/${encodeURIComponent(accountId)}`;

/** 시연 URL 여부 */
export const isDemoRoute = (pathname: string) =>
  pathname === ROUTE_PATHS.DEMO || pathname.startsWith(`${ROUTE_PATHS.DEMO}/`);
